import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  useTheme,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import "./RegistrationPage.less";
import RowContainer from "../../common/RowContainer";
import { useRegisterUserRequestMutation } from "../../../redux/features/auth/authApi";
import { StyledPaddingColumnContainer } from "../../common/ColumnContainer";

import { useFormik } from "formik";
import * as Yup from "yup";
import { FormattedMessage } from "react-intl";
import ModalComponent from "../../common/ModalComponent";
import RegistrationThankYou from "./RegistrationThankYou";
import { useNavigate } from "react-router-dom";
import { commonRoutes } from "../../../utils/routes/Routes";
import { useSelector } from "react-redux";
import { selectLanguage } from "../../../redux/features/language/languageSlice";

const RegistrationPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const currentLanguage = useSelector(selectLanguage);
  const [
    registerUserRequest,
    { data: registrationData, isError, isLoading, isSuccess, error },
  ] = useRegisterUserRequestMutation();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    company_name: "",
    address: "",
    city: "",
    zip_code: "",
    country: "",
    vat_number: "",
    phone: "",
    is_slave: false,
  });

  const handleRegistrationSubmit = async (values) => {
    const userRegistrationData = {
      ...values,
      language: currentLanguage,
    };
    await registerUserRequest(userRegistrationData);
  };
  const handleCloseAndReset = () => {
    navigate(commonRoutes.login);
  };
  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .min(2, <FormattedMessage id="register.form.warnings.field" />)
      .required(<FormattedMessage id="register.form.warnings.empty" />),
    last_name: Yup.string()
      .min(2, <FormattedMessage id="register.form.warnings.field" />)
      .required(<FormattedMessage id="register.form.warnings.empty" />),
    email: Yup.string()
      .email(<FormattedMessage id="register.form.warnings.field" />)
      .required(<FormattedMessage id="register.form.warnings.empty" />),
    company_name: Yup.string().required(
      <FormattedMessage id="register.form.warnings.empty" />
    ),
    address: Yup.string().required(
      <FormattedMessage id="register.form.warnings.empty" />
    ),
    city: Yup.string()
      .min(2, <FormattedMessage id="register.form.warnings.field" />)
      .required(<FormattedMessage id="register.form.warnings.empty" />),
    zip_code: Yup.number()
      .typeError(<FormattedMessage id="register.form.warnings.number.type" />)
      .test(
        "len",
        <FormattedMessage id="register.form.warnings.field" />,
        (val) => val && val.toString().length === 5
      )
      .required(<FormattedMessage id="register.form.warnings.empty" />),
    vat_number: Yup.string()
      .min(11, <FormattedMessage id="register.form.warnings.field" />)
      .required(<FormattedMessage id="register.form.warnings.empty" />),
    phone: Yup.number()
      .typeError(<FormattedMessage id="register.form.warnings.number.type" />)
      .test(
        "len",
        <FormattedMessage id="register.form.warnings.field" />,
        (val) => val && val.toString().length === 10
      )
      .required(<FormattedMessage id="register.form.warnings.empty" />),
  });
  const formik = useFormik({
    initialValues: formData,
    validationSchema: validationSchema,
    onSubmit: handleRegistrationSubmit,
  });

  const formItems = [
    {
      label: <FormattedMessage id="register.form.firstname" />,
      type: "text",
      name: "first_name",
      xs: 12,
      sm: 6,
    },
    {
      label: <FormattedMessage id="register.form.lastname" />,
      type: "text",
      name: "last_name",
      xs: 12,
      sm: 6,
    },
    {
      label: <FormattedMessage id="register.form.email" />,
      type: "email",
      name: "email",
      xs: 12,
      sm: 6,
    },
    {
      label: <FormattedMessage id="register.form.company" />,
      type: "text",
      name: "company_name",
      xs: 12,
      sm: 6,
    },
    {
      label: <FormattedMessage id="register.form.address" />,
      type: "text",
      name: "address",
      xs: 12,
      sm: 12,
    },
    {
      label: <FormattedMessage id="register.form.city" />,
      type: "text",
      name: "city",
      xs: 12,
      sm: 6,
    },
    {
      label: <FormattedMessage id="register.form.zip" />,
      type: "text",
      name: "zip_code",
      xs: 12,
      sm: 6,
    },
    {
      label: <FormattedMessage id="register.form.country" />,
      type: "text",
      name: "country",
      xs: 12,
      sm: 12,
    },
    {
      label: <FormattedMessage id="register.form.vat" />,
      type: "text",
      name: "vat_number",
      xs: 12,
      sm: 12,
    },
    {
      label: <FormattedMessage id="register.form.phone" />,
      type: "text",
      name: "phone",
      xs: 12,
      sm: 12,
    },
    {
      label: <FormattedMessage id="register.form.isSlave" />,
      type: "checkbox",
      name: "is_slave",
      xs: 6,
      sm: 6,
    },
  ];

  return (
    <div className="form-container">
      <RowContainer center>
        <Typography variant="h5">
          <FormattedMessage id="register.title" />
        </Typography>
      </RowContainer>

      <form onSubmit={formik.handleSubmit}>
        <StyledPaddingColumnContainer>
          <Grid
            container
            spacing={3}
            rowSpacing={5}
            className="field-container"
          >
            {formItems.map((item, index) => {
              return item.type === "checkbox" ? (
                <Grid item xs={item.xs} sm={item.sm} key={index}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={item.name}
                        checked={formik.values[item.name]}
                        onChange={formik.handleChange}
                      />
                    }
                    label={
                      <Typography
                        variant="p"
                        component="p"
                        color="var(--text-primary)"
                      >
                        {item.label}
                      </Typography>
                    }
                  />
                </Grid>
              ) : (
                <Grid item xs={item.xs} sm={item.sm}>
                  <TextField
                    size="small"
                    autoComplete="off"
                    required
                    fullWidth
                    label={item.label}
                    type={item.type}
                    name={item.name}
                    value={formik.values[item.name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched[item.name] &&
                      Boolean(formik.errors[item.name])
                    }
                    helperText={
                      formik.touched[item.name] && formik.errors[item.name]
                    }
                  />
                </Grid>
              );
            })}
          </Grid>

          <Grid item xs={12} sx={{ overflow: "hidden" }}>
            {isError ? (
              <Typography
                className="error-message"
                paragraph
                style={{
                  color: theme.palette.error.main,
                }}
              >
                <FormattedMessage id="register.form.error" />
              </Typography>
            ) : null}
            <RowContainer center>
              <Button
                sx={{ width: "30%" }}
                type="submit"
                variant="contained"
                color="primary"
                disabled={!formik.isValid}
              >
                {isLoading ? (
                  <CircularProgress color="inherit" size={"30px"} />
                ) : (
                  <span>
                    <FormattedMessage id="register.form.submit" />
                  </span>
                )}
              </Button>
            </RowContainer>
          </Grid>
        </StyledPaddingColumnContainer>
      </form>
      {isSuccess ? (
        <ModalComponent open={true} handleClose={handleCloseAndReset}>
          <RegistrationThankYou handleClose={handleCloseAndReset} />
        </ModalComponent>
      ) : null}
    </div>
  );
};

export default RegistrationPage;
